import type { ContentTextProps } from '@fortum/elemental-ui'
import { ContentText, fontSizes, fontWeights, spacing } from '@fortum/elemental-ui'

import { StyledLink } from '@/shared/components/StyledLink'
import type { LinkEntry } from '@/shared/contentful/types'
import type { LinkHref } from '@/shared/contentful/utils'
import { getLinkEntryUrl } from '@/shared/contentful/utils'
import { useTheme } from '@/shared/hooks/useTheme'
import { useLocale } from '@/shared/locale'

type HeadingProps = ContentTextProps & {
  /** title link entry */
  titleLink: LinkEntry
  /** full href string for comparison */
  href?: LinkHref
  /** whether this title link matches the current path */
  active?: boolean
}

export default function Heading({ titleLink, href, active, ...otherProps }: HeadingProps) {
  const theme = useTheme()
  const locale = useLocale()
  const entryUrl =
    href ||
    getLinkEntryUrl(titleLink, locale, {
      asString: true,
      withLocalePrefixIfNeeded: true,
    })

  const contentText = (
    <ContentText
      fontSize={fontSizes.xl.default}
      ph={{ xl: spacing.xxs }}
      fontWeight={fontWeights.medium}
      {...otherProps}
    >
      {titleLink.label}
    </ContentText>
  )

  return (
    <>
      {entryUrl !== '' ? (
        <StyledLink
          href={entryUrl}
          color={theme.colors.textPrimary}
          data-testid={`title-link-${titleLink.label}`}
          noUnderline
          aria-current={active ? 'page' : undefined}
        >
          {contentText}
        </StyledLink>
      ) : (
        contentText
      )}
    </>
  )
}
